import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { PropTypes } from 'prop-types';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import {
  editSecret,
  testAzureConnection,
  testDbConnection,
  getSecretsById,
  testS3Connection,
  testSnowflakeConnection,
} from "../../api's/ConnectorsApi";
import LoadingIcon from '../../reusable-component/LoadingIcon';

const INITIAL_STATE = {
  secretName: '',
  connectorType: '',
  engine: '',
  storageAccountKey: '',
  storageAccount: '',
  containerName: '',
  endpointSuffix: '',
  host: '',
  port: '',
  database: '',
  username: '',
  password: '',
  serviceName: '',
  bucketName: '',
  awsAccessKey: '',
  awsSecretKey: '',
  awsSessionToken: '',
  region: '',
  account: '',
  warehouse: '',
  role: '',
};

const EditConnectorForm = ({ open, handleClose, connector, id, secretName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [isConnectionSucceeded, setIsConnectionSucceeded] = useState(false);
  const [isSessionTokenRequired, setIsSessionTokenRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name !== 'secretName') {
      setIsConnectionSucceeded(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== '' && value !== null)
      );
      const secretResponse = await editSecret(cleanedFormData);
      if (secretResponse.status === 201 || secretResponse.status === 200) {
        enqueueSnackbar(secretResponse?.data, {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        handleClose();
      }
    } catch (error) {
      setLoading(false);
      if (error.response.status === 403) {
        enqueueSnackbar(`You don't have Permission !!`, {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else {
        enqueueSnackbar('Test Connection Failed', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    }

    setLoading(false);
  };

  const testConnection = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const cleanedFormData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== '' && value !== null)
      );
      let response;
      switch (connector) {
        case 'adls':
          response = testAzureConnection(cleanedFormData);
          break;
        case 's3':
          response = await testS3Connection(cleanedFormData);
          break;
        case 'snowflake':
          response = await testSnowflakeConnection(cleanedFormData);
          break;
        default:
          response = await testDbConnection(cleanedFormData);
          break;
      }

      if (response.status === 200) {
        enqueueSnackbar('Test Connection Successful', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        setIsConnectionSucceeded(true);
      }
    } catch (error) {
      setLoading(false);
      if (error.response.status === 403) {
        enqueueSnackbar(`You don't have Permission !!`, {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      } else {
        enqueueSnackbar('Test Connection Failed', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    }
    setLoading(false);
  };

  const handleSessionTokenChange = (e) => {
    const value = e.target.value === 'yes';
    setIsSessionTokenRequired(value);
    if (!value) {
      setFormData((prev) => ({
        ...prev,
        awsSessionToken: '', // Clear session token when "No" is selected
      }));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const fetchConnectorDetails = async () => {
    setLoading(true);
    try {
      const response = await getSecretsById(id);
      if (response.status === 200) {
        const responseData = response.data;

        setFormData((prevFormData) => ({
          ...prevFormData,
          ...Object.keys(prevFormData).reduce((acc, key) => {
            if (Object.prototype.hasOwnProperty.call(responseData, key)) {
              acc[key] = responseData[key];
            }
            return acc;
          }, {}),
        }));
      }
    } catch (error) {
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    console.log(connector);
    setFormData((prevFormData) => ({
      ...prevFormData,
      engine: connector,
      connectorType: connector,
      secretName,
    }));
    fetchConnectorDetails();
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Update Connection Details
        <IconButton onClick={handleClose} size="small" sx={{ color: 'red' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form autoComplete="off" onSubmit={testConnection}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {connector === 'adls' && (
              <TextField
                label="Storage Account Key"
                name="storageAccountKey"
                value={formData.storageAccountKey}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'adls' && (
              <TextField
                label="Storage Account"
                name="storageAccount"
                value={formData.storageAccount}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'adls' && (
              <TextField
                label="Container Name"
                name="containerName"
                value={formData.containerName}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'adls' && (
              <TextField
                label="Endpoint Suffix"
                name="endpointSuffix"
                value={formData.endpointSuffix}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {(connector === 'oracle' || connector === 'postgresql' || connector === 'sqlserver') && (
              <TextField
                label="Host Name"
                name="host"
                value={formData.host}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {(connector === 'oracle' || connector === 'postgresql' || connector === 'sqlserver') && (
              <TextField
                label="Port"
                name="port"
                value={formData.port}
                onChange={handleChange}
                type="number"
                required
                fullWidth
              />
            )}
            {(connector === 'postgresql' || connector === 'sqlserver' || connector === 'snowflake') && (
              <TextField
                label="Database Name"
                name="database"
                value={formData.database}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {(connector === 'oracle' ||
              connector === 'postgresql' ||
              connector === 'sqlserver' ||
              connector === 'snowflake') && (
              <TextField
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {(connector === 'oracle' ||
              connector === 'postgresql' ||
              connector === 'sqlserver' ||
              connector === 'snowflake') && (
              <TextField
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {showPassword ? (
                        <VisibilityOff onClick={handleClickShowPassword} style={{ cursor: 'pointer' }} />
                      ) : (
                        <Visibility onClick={handleClickShowPassword} style={{ cursor: 'pointer' }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {connector === 'oracle' && (
              <TextField
                label="Service Name"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleChange}
                required
                fullWidth
              />
            )}

            {connector === 's3' && (
              <TextField
                label="Bucket Name"
                name="bucketName"
                value={formData.bucketName}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 's3' && (
              <TextField
                label="Aws Access Key"
                name="awsAccessKey"
                value={formData.awsAccessKey}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 's3' && (
              <TextField
                label="Aws Secret Key"
                name="awsSecretKey"
                value={formData.awsSecretKey}
                onChange={handleChange}
                required
                fullWidth
              />
            )}

            {connector === 's3' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormLabel sx={{ whiteSpace: 'nowrap', ml: '4px' }}>Use AWS Session Token?</FormLabel>
                <RadioGroup row value={isSessionTokenRequired ? 'yes' : 'no'} onChange={handleSessionTokenChange}>
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
            )}

            {connector === 's3' && (
              <TextField
                label="Aws Session Token"
                name="awsSessionToken"
                value={formData.awsSessionToken}
                onChange={handleChange}
                fullWidth
                disabled={!isSessionTokenRequired}
              />
            )}
            {connector === 's3' && (
              <TextField
                label="Aws Region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'snowflake' && (
              <TextField
                label="Warehouse"
                name="warehouse"
                value={formData.warehouse}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'snowflake' && (
              <TextField
                label="Account"
                name="account"
                value={formData.account}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
            {connector === 'snowflake' && (
              <TextField label="Role" name="role" required value={formData.role} onChange={handleChange} fullWidth />
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button type="submit" variant="contained" color="primary" sx={{ mr: 2 }}>
              Test
            </Button>

            <Button
              type="button"
              onClick={handleSubmit}
              variant="contained"
              color="success"
              disabled={!isConnectionSucceeded}
            >
              Save
            </Button>
          </Box>
        </form>
      </DialogContent>
      {loading && <LoadingIcon />}
    </Dialog>
  );
};

EditConnectorForm.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  service: PropTypes.object,
};

export default EditConnectorForm;
